/**
 * Google Login Button Component
 * Provides OAuth sign-in for authenticated credit features
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';

const GoogleLoginButton = ({ 
  className = '', 
  size = 'medium',
  variant = 'primary',
  showText = true,
  disabled = false,
  onSuccess = null,
  onError = null
}) => {
  const { signInWithGoogle, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = () => {
    try {
      setIsLoading(true);
      // signInWithGoogle just redirects to OAuth, so we won't reach code after this
      signInWithGoogle();
    } catch (error) {
      setIsLoading(false);
      
      if (onError) {
        onError(error);
      }
    }
  };

  // Size classes
  const sizeClasses = {
    small: 'px-3 py-2 text-sm',
    medium: 'px-4 py-2.5 text-base',
    large: 'px-6 py-3 text-lg'
  };

  // Variant classes
  const variantClasses = {
    primary: 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 shadow-sm',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300',
    outline: 'bg-transparent hover:bg-gray-50 text-gray-700 border-2 border-gray-300'
  };

  const buttonClasses = `
    inline-flex items-center justify-center
    font-medium rounded-lg
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `.trim();

  const isButtonDisabled = disabled || loading || isLoading;

  return (
    <button
      onClick={handleGoogleSignIn}
      disabled={isButtonDisabled}
      className={buttonClasses}
      type="button"
    >
      {isLoading ? (
        <>
          {/* Loading spinner */}
          <svg 
            className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Signing in...
        </>
      ) : (
        <>
          {/* Google icon */}
          <svg 
            className="w-5 h-5 mr-2" 
            viewBox="0 0 24 24"
          >
            <path 
              fill="#4285f4" 
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path 
              fill="#34a853" 
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path 
              fill="#fbbc05" 
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path 
              fill="#ea4335" 
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          
          {showText && (
            <span>Sign in with Google</span>
          )}
        </>
      )}
    </button>
  );
};

export default GoogleLoginButton;